import { SystemSettings, LlmProfile } from '@/types';
import { apiClient } from './client';

/**
 * Settings API service
 */
export const settingsApi = {
  /**
   * Get system settings
   */
  async get(): Promise<SystemSettings> {
    const { data } = await apiClient.get<SystemSettings>('/settings');
    return data;
  },

  /**
   * Update system settings
   */
  async update(settings: Partial<SystemSettings>): Promise<SystemSettings> {
    const { data } = await apiClient.put<SystemSettings>('/settings', settings);
    return data;
  },

  /**
   * Get available LLM providers
   */
  async getLlmProviders(): Promise<
    Array<{
      provider: string;
      models: string[];
      capabilities: string[];
    }>
  > {
    const { data } = await apiClient.get('/settings/llm-providers');
    return data;
  },

  /**
   * Test LLM configuration
   */
  async testLlmConfig(profile: LlmProfile): Promise<{
    success: boolean;
    message?: string;
    latency?: number;
  }> {
    const { data } = await apiClient.post('/settings/test-llm', profile);
    return data;
  },

  /**
   * Get tenant settings
   */
  async getTenantSettings(): Promise<{
    tenantId: string;
    name: string;
    subscriptionLevel: string;
    limits: {
      maxConnectors: number;
      maxDocumentsPerRun: number;
      maxParallelRuns: number;
    };
  }> {
    const { data } = await apiClient.get('/settings/tenant');
    return data;
  },

  /**
   * Export settings configuration
   */
  async exportConfig(): Promise<Blob> {
    const response = await apiClient.get('/settings/export', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Import settings configuration
   */
  async importConfig(file: File): Promise<{
    success: boolean;
    imported: string[];
    errors?: string[];
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await apiClient.post('/settings/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },
};
